import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";

export default function Lightbox({ items, index, onClose, onStep }) {
  const open = index !== null && index >= 0;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onStep(1);
      if (e.key === "ArrowLeft") onStep(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, onStep]);

  const item = open ? items[index] : null;

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={item.caption}
          data-testid="lightbox"
          className="fixed inset-0 z-[100] flex flex-col bg-[#0B0C0E]/96 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
        >
          <div className="flex items-center justify-between px-6 py-5 lg:px-12">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#9EA3B0]">
              {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")} · {item.caption}
            </span>
            <button
              data-testid="lightbox-close"
              onClick={onClose}
              aria-label="Close"
              className="text-[#9EA3B0] transition-colors hover:text-white"
            >
              <X size={22} />
            </button>
          </div>
          <div className="flex flex-1 items-center justify-center overflow-auto px-4 pb-6 lg:px-12" onClick={(e) => e.stopPropagation()}>
            <motion.img
              key={item.src}
              src={item.src}
              alt={item.caption}
              data-testid="lightbox-image"
              className="max-h-full w-auto max-w-full border border-white/10 object-contain"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <div className="flex items-center justify-between px-6 py-5 lg:px-12" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="lightbox-prev"
              onClick={() => onStep(-1)}
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#9EA3B0] transition-colors hover:text-white"
            >
              <ArrowLeft size={14} /> Prev
            </button>
            <button
              data-testid="lightbox-next"
              onClick={() => onStep(1)}
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#9EA3B0] transition-colors hover:text-white"
            >
              Next <ArrowRight size={14} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
